import {Route, Routes} from "react-router-dom";
import {pathToCriterionName} from "@/utils/criteria-helper.tsx";
import {UserRole} from "@/types/UserRole.ts";
import DefaultLayout from "@/layouts/DefaultLayout.tsx";
import ProtectedLayout from "@/layouts/ProtectedLayout.tsx";
import ProtectedRouteWithId from "@/layouts/ProtectedLayoutWithId.tsx";
import LandingPage from "@/pages/LandingPage.tsx";
import DashboardPage from "@/pages/DashboardPage.tsx";
import MatchCriteriaLayout from "@/pages/match-criteria/layout.tsx";
import CriteriaPage from "@/pages/match-criteria/page.tsx";
import AffinityGroupsPage from "@/pages/affinity-groups/page.tsx";
import SettingsLayout from "@/pages/settings/layout.tsx";
import GeneralSettingsPage from "@/pages/settings/GeneralSettingsPage.tsx";
import FAQPage from "@/pages/settings/FAQPage.tsx";
import ScheduledJobsPage from "@/pages/settings/ScheduledJobsPage.tsx";
import UsersPage from "@/pages/users/index.tsx";
import UserProfile from "@/pages/users/UserProfile.tsx";
import MentorsPage from "@/pages/mentors";
import MenteesPage from "@/pages/mentees";
import MentorProfilePage from "@/pages/mentors/profile/MentorProfilePage.tsx";
import MentorProfileUpdateLayout from "@/pages/mentors/profile/layout.tsx";
import MentorProfileUpdateGeneral from "@/pages/mentors/profile/MentorProfileUpdateGeneral.tsx";
import MentorProfileUpdateDetails from "@/pages/mentors/profile/MentorProfileUpdateDetails.tsx";
import MentorProfileUpdateAvailability from "@/pages/mentors/profile/MentorProfileUpdateAvailability.tsx";
import MentorMenteesPage from "@/pages/mentors/mentees";
import MenteeProfilePage from "@/pages/mentees/MenteeProfilePage.tsx";
import MenteeProfileEdit from "@/pages/mentees/profile/MenteeProfileEdit.tsx";
import PreferenceForm from "@/pages/mentees/PreferenceForm.tsx";
import ErrorPage from "@/pages/ErrorPage.tsx";
import SuggestionsLayout from "@/pages/suggestions/layout.tsx";
import MenteesSuggestions from "@/pages/suggestions/MenteesSuggestions.tsx";
import MentorsSuggestions from "@/pages/suggestions/MentorsSuggestions.tsx";
import SessionAttendancePage from "@/pages/session-attendance/SessionAttendancePage.tsx";
import AuditLogPage from "@/pages/audit-log/AuditLogPage.tsx";

const App = () => (
    <Routes>
        <Route element={<DefaultLayout />}>
            {/* public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/error" element={<ErrorPage/>}/>
        </Route>

        <Route element={<ProtectedLayout allowedRoles={[UserRole.ADMIN, UserRole.MENTOR, UserRole.MENTEE]}/>}>
            <Route path="/dashboard" element={<DashboardPage/>}/>
            <Route path="/mentors/:id" element={<MentorProfilePage/>}/>
            <Route path="/mentees/:id" element={<MenteeProfilePage/>}/>
            <Route path="/mentors/:id/mentees" element={<MentorMenteesPage/>}/>
            <Route path="/session-attendance" element={<SessionAttendancePage/>}/>
        </Route>
        <Route element={<ProtectedLayout allowedRoles={[UserRole.ADMIN]}/>}>
            <Route path="/users" element={<UsersPage/>}/>
            <Route path="/mentors" element={<MentorsPage/>}/>
            <Route path="/mentees" element={<MenteesPage/>}/>
            <Route path="/suggestions/*" element={<SuggestionsRoute/>}/>
            <Route path="/settings/*" element={<SettingsRoute/>}/>
            <Route path="/match-criteria/*" element={<MatchCriteriaRoute/>}/>
            <Route path="/audit-log" element={<AuditLogPage/>}/>
        </Route>
        <Route element={<ProtectedRouteWithId />}>
            <Route path="/users/:id" element={<UserProfile/>}/>
        </Route>
        <Route element={<ProtectedRouteWithId roleType={UserRole.MENTOR} />}>
            <Route path="/mentors/:id/edit/*" element={<MentorProfileUpdateRoute/>}/>
        </Route>
        <Route element={<ProtectedRouteWithId roleType={UserRole.MENTEE} />}>
            <Route path="/mentees/:id/preference" element={<PreferenceForm/>}/>
            <Route path="/mentees/:id/edit" element={<MenteeProfileEdit/>}/>
        </Route>

        {/* catch all */}
        <Route path="*" element={<ErrorPage httpCode="404" errorTitle="pageNotFound"
                                            errorMessage="pageNotFoundDesc"/>}/>
    </Routes>
);

const MatchCriteriaRoute = () => {
    return (
        <MatchCriteriaLayout>
            <Routes>
                <Route path="/" element={<CriteriaPage/>}/>
                <Route path="/affinity-groups" element={<AffinityGroupsPage/>}/>
                {Object.keys(pathToCriterionName).map((key) => (
                    <Route
                        key={key}
                        path={`/${key}`}
                        element={<CriteriaPage/>}
                    />
                ))}
            </Routes>
        </MatchCriteriaLayout>
    );
};

const MentorProfileUpdateRoute = () => {
    return (
        <MentorProfileUpdateLayout>
            <Routes>
                <Route path="/" element={<MentorProfileUpdateGeneral/>}/>
                <Route path="/details" element={<MentorProfileUpdateDetails/>}/>
                <Route path="/availability" element={<MentorProfileUpdateAvailability/>}/>
            </Routes>
        </MentorProfileUpdateLayout>
    );
};

const SettingsRoute = () => {
    return (
        <SettingsLayout>
            <Routes>
                <Route path="/" element={<GeneralSettingsPage/>}/>
                <Route path="/faq" element={<FAQPage/>}/>
                <Route path="/jobs" element={<ScheduledJobsPage/>}/>
            </Routes>
        </SettingsLayout>
    );
};

const SuggestionsRoute = () => {
    return (
        <SuggestionsLayout>
            <Routes>
                <Route path="/" element={<MenteesSuggestions/>}/>
                <Route path="/mentors" element={<MentorsSuggestions/>}/>
            </Routes>
        </SuggestionsLayout>
    );
};

export default App;
